import { useEffect, useRef, useState, useCallback } from "react";
import {
  Camera,
  RefreshCw,
  Timer,
  SlidersHorizontal,
  Download,
  X,
  RotateCcw,
  ImageOff,
  Share2,
  Images,
  Zap,
  ZapOff,
  Maximize2,
  HardDriveDownload,
  HelpCircle,
  Crown,
  MoreVertical,
} from "lucide-react";
import { toast } from "sonner";
import FilterPanel from "@/components/FilterPanel";
import Gallery from "@/components/Gallery";
import WhereSavedHint from "@/components/WhereSavedHint";
import { addPhoto, dataUrlToBlob, countPhotos } from "@/lib/idb";
import {
  autoSavePhoto,
  pickRealSelfieFolder,
  getActiveFolder,
  isFsApiSupported,
} from "@/lib/autosave";
import {
  initDevice,
  reportPhoto,
  createCheckout,
  getCheckoutStatus,
} from "@/lib/api";
import PaywallModal from "@/components/PaywallModal";
import SettingsMenu from "@/components/SettingsMenu";
import { useOrientation } from "@/lib/orientation";
import { openExternal, isCapacitorNative } from "@/lib/platform";
import { t } from "@/lib/i18n";
import { enableContinuousFocus, takeFullResPhoto } from "@/lib/hiresCapture";

const DEFAULT_FILTERS = {
  brightness: 100, // %
  contrast: 100,
  saturation: 100,
  warmth: 0, // -100..100 (sepia mapping)
};

// Preset configs (key -> filter object) — labels resolved via i18n
export const FILTER_PRESETS = {
  original: { labelKey: "presetOriginal", values: { brightness: 100, contrast: 100, saturation: 100, warmth: 0 } },
  bw:       { labelKey: "presetBw",       values: { brightness: 105, contrast: 115, saturation: 0,   warmth: 0 } },
  vintage:  { labelKey: "presetVintage",  values: { brightness: 105, contrast: 95,  saturation: 85,  warmth: 55 } },
  softskin: { labelKey: "presetSoft",     values: { brightness: 108, contrast: 92,  saturation: 95,  warmth: 12 } },
  cinema:   { labelKey: "presetCinema",   values: { brightness: 95,  contrast: 125, saturation: 88,  warmth: -25 } },
};

const TIMER_OPTIONS = [0, 3, 5, 10];

// Aspect ratio options. ratio = target width / target height (portrait orientation).
export const ASPECT_RATIOS = [
  { key: "full", label: "Full", ratio: null },
  { key: "1:1", label: "1:1", ratio: 1 },
  { key: "4:3", label: "4:3", ratio: 3 / 4 },
  { key: "16:9", label: "16:9", ratio: 9 / 16 },
];

// Compute source crop rectangle from video (vw,vh) for a target portrait ratio
function getCropRect(vw, vh, targetRatio) {
  if (!targetRatio) return { sx: 0, sy: 0, sw: vw, sh: vh };
  const sourceRatio = vw / vh;
  if (sourceRatio > targetRatio) {
    // source is wider than target -> crop horizontally
    const sw = vh * targetRatio;
    const sh = vh;
    const sx = (vw - sw) / 2;
    return { sx, sy: 0, sw, sh };
  }
  // source taller than target -> crop vertically
  const sw = vw;
  const sh = vw / targetRatio;
  const sy = (vh - sh) / 2;
  return { sx: 0, sy, sw, sh };
}

const buildFilterString = (f) => {
  const sepia = Math.max(0, f.warmth) / 100; // warm => sepia
  const hueRotate = f.warmth < 0 ? Math.round(f.warmth * 0.6) : 0; // cool => slight hue shift
  return [
    `brightness(${f.brightness}%)`,
    `contrast(${f.contrast}%)`,
    `saturate(${f.saturation}%)`,
    sepia > 0 ? `sepia(${sepia})` : null,
    hueRotate !== 0 ? `hue-rotate(${hueRotate}deg)` : null,
  ]
    .filter(Boolean)
    .join(" ");
};

export default function CameraApp() {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState("user");
  const [timerSec, setTimerSec] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [flash, setFlash] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [activePreset, setActivePreset] = useState("original");
  const [capturedImage, setCapturedImage] = useState(null);
  const [savedPhotoId, setSavedPhotoId] = useState(null);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryCount, setGalleryCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const capturingRef = useRef(false);
  const [aspect, setAspect] = useState("full");
  const [frontFlash, setFrontFlash] = useState(false);
  const [preFlash, setPreFlash] = useState(false);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(() => {
    if (typeof localStorage === "undefined") return true;
    const v = localStorage.getItem("rs_autosave");
    return v === null ? true : v === "1";
  });
  const [autoShareEnabled, setAutoShareEnabled] = useState(() => {
    if (typeof localStorage === "undefined") return false;
    return localStorage.getItem("rs_autoshare") === "1";
  });
  const [sharePending, setSharePending] = useState(false);
  const [hasFolder, setHasFolder] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const orientation = useOrientation();
  const isLandscape = orientation !== "portrait";
  const isLandscapeLeft = orientation === "landscape-left";
  const isLandscapeRight = orientation === "landscape-right";
  // Paywall state
  const [photoCount, setPhotoCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [freeLimit, setFreeLimit] = useState(5);
  const [showPaywall, setShowPaywall] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const filterString = buildFilterString(filters);
  // Mirror preview only when using front camera
  const isMirroredPreview = facingMode === "user";

  // Stop current stream
  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  // Start camera
  const startCamera = useCallback(
    async (mode) => {
      setError(null);
      setReady(false);
      stopStream();
      try {
        const constraints = {
          audio: false,
          video: {
            facingMode: { ideal: mode },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        enableContinuousFocus(stream.getVideoTracks()[0]);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setReady(true);
      } catch (e) {
        console.error(e);
        let msg = t("cameraGeneric");
        if (e?.name === "NotAllowedError")
          msg = t("permissionDenied");
        else if (e?.name === "NotFoundError") msg = t("noCamera");
        else if (e?.name === "NotReadableError")
          msg = t("cameraBusy");
        setError(msg);
      }
    },
    [stopStream]
  );

  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) startCamera(facingMode);
    });
    return () => {
      cancelled = true;
      stopStream();
    };
  }, [facingMode, startCamera, stopStream]);

  // Restart the camera when the app returns from background — Android WebView
  // (and iOS Safari) stop the MediaStream when the app is minimised or the
  // screen is locked, which would otherwise leave a frozen last frame or a
  // black canvas after resume. We also stop the stream when the app goes
  // to background so it releases the camera hardware for other apps.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Only re-init if the tracks are dead — avoids restarting when the
        // user just opened a modal on top of the same tab.
        const tracks = streamRef.current?.getTracks?.() || [];
        const anyLive = tracks.some((t) => t.readyState === "live");
        if (!anyLive && !capturedImage) {
          startCamera(facingMode);
        }
      } else {
        stopStream();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onVisibility);
    };
  }, [facingMode, startCamera, stopStream, capturedImage]);

  // Hardware Back button (Android) → close the top-most open modal instead
  // of exiting the app. Capacitor's hardware Back does NOT reliably trigger
  // `popstate` on all devices; we listen to the Capacitor `App.backButton`
  // event directly when running natively, and fall back to `popstate` (with
  // a pushed sentinel) in a plain web browser.
  const modalRefs = useRef({});
  modalRefs.current = {
    showHint,
    showPaywall,
    showGallery,
    showFilters,
    showSettings,
    capturedImage,
  };

  // Priority-ordered closer. Returns true if it closed something.
  const closeTopModal = useCallback(() => {
    const s = modalRefs.current;
    if (s.showHint) { setShowHint(false); return true; }
    if (s.showPaywall) { setShowPaywall(false); return true; }
    if (s.showGallery) { setShowGallery(false); return true; }
    if (s.showFilters) { setShowFilters(false); return true; }
    if (s.showSettings) { setShowSettings(false); return true; }
    if (s.capturedImage) {
      setCapturedImage(null);
      setSavedPhotoId(null);
      return true;
    }
    return false;
  }, []);

  // --- Capacitor native hardware Back button ---
  // The Capacitor App plugin exposes a synchronous "backButton" event that
  // fires on hardware Back inside the APK. Its `canGoBack` payload reflects
  // WebView history. We consume the event unconditionally when any modal is
  // open (close it), and only exit the app when there's nothing left to
  // close and no WebView history to navigate back to.
  useEffect(() => {
    let sub = null;
    let cancelled = false;
    if (isCapacitorNative()) {
      import("@capacitor/app").then(({ App }) => {
        if (cancelled) return;
        App.addListener("backButton", ({ canGoBack }) => {
          if (closeTopModal()) return;                  // menu was open → closed it
          if (canGoBack) window.history.back();          // internal SPA back
          else App.exitApp();                            // nothing left → exit
        }).then((s) => {
          if (cancelled) s.remove();
          else sub = s;
        });
      });
    }
    return () => {
      cancelled = true;
      if (sub) sub.remove();
    };
  }, [closeTopModal]);

  // --- Browser fallback: history sentinel + popstate ---
  // In a plain web browser (or PWA that isn't inside Capacitor) we can only
  // observe Back via popstate. We push a single sentinel while any modal is
  // open so the browser Back closes it instead of navigating away.
  const backSentinelRef = useRef(false);
  const anyModalOpen =
    showSettings ||
    showFilters ||
    showGallery ||
    showPaywall ||
    showHint ||
    !!capturedImage;

  useEffect(() => {
    if (isCapacitorNative()) return; // native path handles this
    if (anyModalOpen && !backSentinelRef.current) {
      window.history.pushState({ __rsModal: 1 }, "");
      backSentinelRef.current = true;
    } else if (!anyModalOpen && backSentinelRef.current) {
      backSentinelRef.current = false;
      if (window.history.state && window.history.state.__rsModal) {
        window.history.back();
      }
    }
  }, [anyModalOpen]);

  useEffect(() => {
    if (isCapacitorNative()) return;
    const onPop = () => {
      backSentinelRef.current = false;
      closeTopModal();
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [closeTopModal]);

  // After retake, the camera-screen <video> is freshly mounted.
  // Re-attach the still-active stream so videoWidth becomes non-zero again.
  useEffect(() => {
    if (capturedImage) return;
    const v = videoRef.current;
    const s = streamRef.current;
    if (v && s && v.srcObject !== s) {
      v.srcObject = s;
      v.play().catch(() => {});
    }
  }, [capturedImage]);

  // Auto-return to camera after a short preview (1s).
  // Paused while the share sheet is open.
  useEffect(() => {
    if (!capturedImage) return;
    if (sharePending) return;
    const t = setTimeout(() => {
      setCapturedImage(null);
      setSavedPhotoId(null);
    }, 1000);
    return () => clearTimeout(t);
  }, [capturedImage, sharePending]);

  // Vibrate helper
  const haptic = (ms = 12) => {
    if (navigator.vibrate) navigator.vibrate(ms);
  };

  const doCapture = useCallback(async () => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;

    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh || capturingRef.current) return;
    capturingRef.current = true;

    // Optional front-camera screen flash for low light
    if (frontFlash && facingMode === "user") {
      setPreFlash(true);
      await new Promise((r) => setTimeout(r, 220));
    }

    // Compute crop based on aspect ratio. ASPECT_RATIOS stores portrait
    // (W/H) ratios; in landscape we invert so a "4:3" frame the user sees on
    // screen produces a real 4:3 (4/3) crop, not a 3:4 vertical crop.
    const aspectCfg = ASPECT_RATIOS.find((a) => a.key === aspect);
    const targetRatio = aspectCfg?.ratio
      ? isLandscape
        ? 1 / aspectCfg.ratio
        : aspectCfg.ratio
      : undefined;
    const track = streamRef.current?.getVideoTracks?.()[0];
    const photo = await takeFullResPhoto(track, vw, vh);
    const srcW = photo ? photo.width : vw;
    const srcH = photo ? photo.height : vh;
    // Without a chosen ratio, keep the preview's framing.
    const { sx, sy, sw, sh } = getCropRect(srcW, srcH, targetRatio || vw / vh);

    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext("2d");

    // Apply filters to canvas
    ctx.filter = filterString;

    // IMPORTANT: do NOT mirror the canvas — we want the REAL (un-mirrored) photo
    ctx.drawImage(photo || video, sx, sy, sw, sh, 0, 0, sw, sh);
    photo?.close?.();
    capturingRef.current = false;

    // Trigger flash UI
    setFlash(true);
    haptic(20);
    setTimeout(() => {
      setFlash(false);
      setPreFlash(false);
    }, 420);

    const dataUrl = canvas.toDataURL("image/jpeg", 0.95);
    setCapturedImage(dataUrl);

    // Save to IndexedDB gallery automatically
    try {
      const blob = dataUrlToBlob(dataUrl);
      const id = await addPhoto(blob, sw, sh);
      setSavedPhotoId(id);
      const n = await countPhotos();
      setGalleryCount(n);

      // Report photo to backend (paywall counter)
      reportPhoto()
        .then((state) => {
          setPhotoCount(state.photo_count);
          setIsPremium(state.is_premium);
          setFreeLimit(state.free_limit);
          // If just used the last free photo, show paywall pre-emptively
          if (
            !state.is_premium &&
            !state.can_take_more &&
            state.photo_count >= state.free_limit
          ) {
            // small delay so user sees their preview first
            setTimeout(() => setShowPaywall(true), 1100);
          }
        })
        .catch((e) => console.warn("reportPhoto failed:", e));

      // Auto-save to device (Capacitor native, FS API folder, or download fallback)
      if (autoSaveEnabled) {
        autoSavePhoto(blob)
          .then((res) => {
            if (res?.method === "native" || res?.method === "folder") {
              toast.success(t("savedToFolder", res.folderName), { duration: 1400 });
            } else {
              toast.success(t("savedToDownloads"), { duration: 1400 });
            }
          })
          .catch((e) => console.warn("autosave failed:", e));
      }

      // Auto-share — open the native share-sheet so user can save to Photos / Gallery
      if (autoShareEnabled) {
        const file = new File([blob], "real-selfie.jpg", { type: "image/jpeg" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          setSharePending(true);
          navigator
            .share({ files: [file], title: "Real Selfie" })
            .catch(() => {})
            .finally(() => setSharePending(false));
        }
      }
    } catch (e) {
      console.error("Gallery save failed:", e);
    }
  }, [filterString, aspect, frontFlash, facingMode, autoSaveEnabled, autoShareEnabled, isLandscape]);

  const startCountdownThenCapture = useCallback(() => {
    if (busy) return;
    // Paywall: block when free limit hit and not premium
    if (!isPremium && photoCount >= freeLimit) {
      setShowPaywall(true);
      return;
    }
    if (timerSec === 0) {
      doCapture();
      return;
    }
    setBusy(true);
    let remaining = timerSec;
    setCountdown(remaining);
    haptic(8);
    const tick = () => {
      remaining -= 1;
      if (remaining <= 0) {
        setCountdown(0);
        setBusy(false);
        doCapture();
      } else {
        setCountdown(remaining);
        haptic(8);
        setTimeout(tick, 1000);
      }
    };
    setTimeout(tick, 1000);
  }, [busy, timerSec, doCapture, isPremium, photoCount, freeLimit]);

  const flipCamera = () => {
    setFacingMode((m) => (m === "user" ? "environment" : "user"));
  };

  const cycleTimer = () => {
    setTimerSec((prev) => {
      const idx = TIMER_OPTIONS.indexOf(prev);
      const next = TIMER_OPTIONS[(idx + 1) % TIMER_OPTIONS.length];
      toast(next === 0 ? t("timerOff") : t("timer", next), {
        duration: 900,
      });
      return next;
    });
  };

  const cycleAspect = () => {
    setAspect((prev) => {
      const idx = ASPECT_RATIOS.findIndex((a) => a.key === prev);
      const next = ASPECT_RATIOS[(idx + 1) % ASPECT_RATIOS.length];
      toast(t("aspect", next.label), { duration: 900 });
      return next.key;
    });
  };

  const toggleFrontFlash = () => {
    setFrontFlash((v) => {
      const next = !v;
      toast(next ? t("flashOn") : t("flashOff"), { duration: 900 });
      return next;
    });
  };

  const toggleAutoSave = async () => {
    if (!autoSaveEnabled) {
      // Turning ON. If FS API supported and no handle yet, prompt user to pick folder.
      if (isFsApiSupported() && !hasFolder) {
        const handle = await pickRealSelfieFolder();
        if (handle) {
          setHasFolder(true);
          toast.success(t("savedToFolder", handle.name || "RealSelfie"), {
            duration: 1400,
          });
        } else {
          // User cancelled or denied — still enable, will fall back to downloads
          toast(t("savedToDownloads"), { duration: 1400 });
        }
      } else {
        toast.success(t("autoSaveOn"), { duration: 900 });
      }
      setAutoSaveEnabled(true);
      try {
        localStorage.setItem("rs_autosave", "1");
      } catch {
        // ignore storage errors (private mode)
      }
    } else {
      setAutoSaveEnabled(false);
      try {
        localStorage.setItem("rs_autosave", "0");
      } catch {
        // ignore storage errors
      }
      toast(t("autoSaveOff"), { duration: 900 });
    }
  };

  const toggleAutoShare = () => {
    setAutoShareEnabled((v) => {
      const next = !v;
      try {
        localStorage.setItem("rs_autoshare", next ? "1" : "0");
      } catch {
        // ignore
      }
      toast(next ? t("autoShareOn") : t("autoShareOff"), { duration: 900 });
      return next;
    });
  };

  const downloadImage = async () => {
    if (!capturedImage) return;
    const blob = dataUrlToBlob(capturedImage);
    try {
      // In Capacitor APK + when FS API is supported, autoSavePhoto persists the
      // file to a real on-device folder. In other browsers it triggers the
      // built-in browser download. Either way the user gets a saved JPEG.
      const res = await autoSavePhoto(blob);
      if (res?.method === "native" || res?.method === "folder") {
        toast.success(t("savedToFolder", res.folderName));
      } else {
        toast.success(t("photoSaved"));
      }
    } catch (e) {
      console.error("downloadImage failed:", e);
      toast.error(t("paymentFailed")); // generic error toast
    }
  };

  const shareImage = async () => {
    if (!capturedImage) return;
    try {
      const blob = dataUrlToBlob(capturedImage);
      const file = new File([blob], "real-selfie.jpg", { type: "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Real Selfie",
          text: "Moja reálna selfie fotka",
        });
      } else {
        // Fallback: download
        downloadImage();
        toast(t("shareUnsupported"), { duration: 1400 });
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error(e);
        toast.error(t("shareFail"));
      }
    }
  };

  const retake = () => {
    setCapturedImage(null);
    setSavedPhotoId(null);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setActivePreset("original");
  };

  const applyPreset = (key) => {
    const p = FILTER_PRESETS[key];
    if (!p) return;
    setFilters(p.values);
    setActivePreset(key);
    haptic(6);
  };

  const handleFiltersChange = (next) => {
    setFilters(next);
    setActivePreset("custom");
  };

  // Refresh gallery count on mount
  useEffect(() => {
    countPhotos().then(setGalleryCount).catch(() => {});
    // Check if user already picked a folder previously
    getActiveFolder()
      .then((h) => setHasFolder(!!h))
      .catch(() => {});

    // Init device with backend (paywall)
    initDevice()
      .then((state) => {
        setPhotoCount(state.photo_count);
        setIsPremium(state.is_premium);
        setFreeLimit(state.free_limit);
      })
      .catch((e) => console.warn("initDevice failed:", e));

    // Stripe return: poll status if URL contains ?session_id=
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (sessionId) {
      pollPaymentStatus(sessionId);
      // Clean query so reload doesn't re-poll
      const url = new URL(window.location.href);
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", url.toString());
    }

    // First-run hint about where photos are saved
    try {
      const seen = localStorage.getItem("rs_hint_seen");
      if (!seen) {
        const tm = setTimeout(() => setShowHint(true), 1200);
        return () => clearTimeout(tm);
      }
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const MAX = 12;
    const INTERVAL = 2000;
    if (attempts >= MAX) {
      // Silent — don't scare the user. The webhook may still arrive.
      console.warn("Payment status polling exhausted");
      return;
    }
    try {
      const res = await getCheckoutStatus(sessionId);
      if (res.payment_status === "paid" || res.is_premium) {
        setIsPremium(true);
        setShowPaywall(false);
        toast.success(t("premiumUnlocked"), { duration: 2200 });
        return;
      }
      if (res.status === "expired") {
        // Only show error if user clearly cancelled
        toast(t("paymentFailed"), { duration: 1400 });
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), INTERVAL);
    } catch (e) {
      console.warn("pollPaymentStatus error", e);
      if (attempts < MAX) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), INTERVAL);
      }
    }
  }, []);

  const handleUnlock = useCallback(() => {
    setCheckoutLoading(true);
    createCheckout()
      .then(({ url }) => {
        // openExternal() opens the system browser when running inside the
        // Capacitor Android wrapper (Play Store compliance: 3rd-party payment
        // UI must not render inside the APK WebView). On the web it falls
        // back to a regular same-tab redirect.
        openExternal(url);
      })
      .catch((e) => {
        console.error(e);
        toast.error(t("paymentFailed"));
        setCheckoutLoading(false);
      });
  }, []);

  const dismissHint = () => {
    setShowHint(false);
    try {
      localStorage.setItem("rs_hint_seen", "1");
    } catch {
      // ignore
    }
  };

  // ---- RENDER ----

  if (capturedImage) {
    return (
      <div
        data-testid="captured-screen"
        className="h-[100dvh] w-screen bg-black overflow-hidden relative flex flex-col"
      >
        <div className="absolute top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-gradient-to-b from-black/70 to-transparent">
          <button
            data-testid="retake-button"
            onClick={retake}
            aria-label="Odfotiť znova"
            className="h-12 w-12 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
          >
            <X className="h-5 w-5 text-white" strokeWidth={1.8} />
          </button>
          <div className="flex flex-col items-center">
            <div className="font-mono-label text-white/70">{t("previewReal")}</div>
            {savedPhotoId && (
              <div className="font-mono-label text-emerald-400/80 mt-1">
                {t("savedToGallery")}
              </div>
            )}
          </div>
          <div className="h-12 w-12" />
        </div>

        <div className="flex-1 flex items-center justify-center p-4 pt-24 pb-32">
          <img
            data-testid="captured-image-preview"
            src={capturedImage}
            alt="Captured selfie"
            className="max-h-full max-w-full object-contain rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
          />
        </div>

        <div className="absolute bottom-0 left-0 right-0 z-50 p-8 pb-12 flex items-center justify-around bg-gradient-to-t from-black/90 via-black/50 to-transparent">
          <button
            data-testid="retake-button-bottom"
            onClick={retake}
            className="flex flex-col items-center gap-2 active:scale-95 transition-transform"
          >
            <div className="h-14 w-14 rounded-full bg-white/8 backdrop-blur-md border border-white/15 flex items-center justify-center">
              <RotateCcw className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-mono-label text-white/70">{t("retake")}</span>
          </button>

          <button
            data-testid="share-button"
            onClick={shareImage}
            className="flex flex-col items-center gap-2 active:scale-95 transition-transform"
          >
            <div className="h-16 w-16 rounded-full bg-white flex items-center justify-center shadow-[0_8px_24px_rgba(255,255,255,0.25)]">
              <Share2 className="h-6 w-6 text-black" strokeWidth={2} />
            </div>
            <span className="font-mono-label text-white">{t("share")}</span>
          </button>

          <button
            data-testid="download-button"
            onClick={downloadImage}
            className="flex flex-col items-center gap-2 active:scale-95 transition-transform"
          >
            <div className="h-14 w-14 rounded-full bg-white/8 backdrop-blur-md border border-white/15 flex items-center justify-center">
              <Download className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-mono-label text-white/70">{t("download")}</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="camera-screen"
      className="h-[100dvh] w-screen bg-black overflow-hidden relative"
    >
      {/* VIDEO FEED */}
      <video
        ref={videoRef}
        data-testid="camera-video-feed"
        autoPlay
        playsInline
        muted
        disablePictureInPicture
        controls={false}
        onCanPlay={() => setReady(true)}
        onPlaying={() => setReady(true)}
        className={`absolute inset-0 h-full w-full object-cover ${
          isMirroredPreview ? "video-mirror" : ""
        } ${ready ? "opacity-100" : "opacity-0"} transition-opacity duration-200`}
        style={{ filter: filterString }}
      />
      <canvas ref={canvasRef} className="hidden" />

      {/* Splash cover — hides the WebView's default "click-to-play" video
          placeholder while the camera stream is being initialised. Shows the
          Real Selfie brand mark (icon + wordmark) so the app looks polished
          on cold start and when returning from background. */}
      {!ready && !error && !capturedImage && (
        <div
          data-testid="camera-splash"
          className="absolute inset-0 z-20 bg-black flex flex-col items-center justify-center gap-6"
        >
          <img
            src="/icon-512.png"
            alt="Real Selfie"
            className="h-24 w-24 rounded-3xl shadow-[0_8px_32px_rgba(255,60,48,0.25)]"
          />
          <div className="flex flex-col items-center gap-2">
            <span className="font-display text-2xl font-light text-white tracking-tight">
              Real Selfie
            </span>
            <div className="h-1 w-24 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full w-1/2 bg-gradient-to-r from-transparent via-white/60 to-transparent splash-shimmer" />
            </div>
          </div>
        </div>
      )}

      {/* Grain overlay */}
      <div className="absolute inset-0 grain z-10" />

      {/* Aspect ratio crop overlay (visual guide) */}
      {aspect !== "full" && !error && (
        <AspectMask
          aspectKey={aspect}
          isLandscape={isLandscape}
          isLandscapeLeft={isLandscapeLeft}
        />
      )}

      {/* Error state */}
      {error && (
        <div
          data-testid="camera-error"
          className="absolute inset-0 z-40 flex flex-col items-center justify-center p-8 text-center bg-black"
        >
          <ImageOff className="h-12 w-12 text-white/40 mb-6" strokeWidth={1.2} />
          <h2 className="font-display text-2xl font-light text-white mb-2">
            {t("cameraUnavailable")}
          </h2>
          <p className="text-sm text-white/60 mb-8 max-w-xs">{error}</p>
          <button
            data-testid="retry-camera-button"
            onClick={() => startCamera(facingMode)}
            className="px-6 py-3 rounded-full bg-white text-black font-medium text-sm active:scale-95 transition-transform"
          >
            {t("tryAgain")}
          </button>
        </div>
      )}

      {/* Minimal top status: Premium/Free badge centered (no full top bar) */}
      {!error && (
        <div className="absolute top-4 left-0 right-0 z-30 flex justify-center pointer-events-none">
          {isPremium ? (
            <div
              data-testid="premium-badge"
              className="h-7 px-3 rounded-full bg-gradient-to-r from-amber-300 to-amber-500 text-black flex items-center gap-1.5 shadow-[0_4px_16px_rgba(251,191,36,0.3)]"
            >
              <Crown className="h-3 w-3" strokeWidth={2.2} />
              <span className="font-display text-[10px] font-bold tracking-[0.16em] uppercase">
                {t("premiumBadge")}
              </span>
            </div>
          ) : (
            <button
              data-testid="free-counter"
              onClick={() => setShowPaywall(true)}
              aria-label={t("paywallTitle")}
              className={`pointer-events-auto h-7 px-3 rounded-full font-mono-label border backdrop-blur-md active:scale-95 transition-transform ${
                photoCount >= freeLimit
                  ? "bg-red-500/20 text-red-200 border-red-400/30"
                  : "bg-black/40 text-white/85 border-white/10"
              }`}
            >
              {photoCount >= freeLimit
                ? t("freeUsed")
                : t("freeLeft", freeLimit - photoCount)}
            </button>
          )}
        </div>
      )}

      {/* Countdown overlay */}
      {countdown > 0 && (
        <div
          data-testid="countdown-overlay"
          className="absolute inset-0 z-40 flex items-center justify-center pointer-events-none"
        >
          <div
            key={countdown}
            className="font-display font-black text-white count-pulse drop-shadow-[0_8px_32px_rgba(0,0,0,0.7)]"
            style={{ fontSize: "22vh", lineHeight: 1 }}
          >
            {countdown}
          </div>
        </div>
      )}

      {/* Filter panel */}
      {showFilters && (
        <FilterPanel
          orientation={orientation}
          filters={filters}
          activePreset={activePreset}
          presets={FILTER_PRESETS}
          onChange={handleFiltersChange}
          onApplyPreset={applyPreset}
          onReset={resetFilters}
          onClose={() => setShowFilters(false)}
        />
      )}

      {/* Settings menu (3-dots) */}
      {showSettings && (
        <SettingsMenu
          onClose={() => setShowSettings(false)}
          galleryCount={galleryCount}
          isPremium={isPremium}
          photoCount={photoCount}
          freeLimit={freeLimit}
          timerSec={timerSec}
          aspect={aspect}
          frontFlash={frontFlash}
          autoSaveEnabled={autoSaveEnabled}
          autoShareEnabled={autoShareEnabled}
          showFrontFlash={facingMode === "user"}
          onOpenGallery={() => setShowGallery(true)}
          onCycleTimer={cycleTimer}
          onCycleAspect={cycleAspect}
          onToggleFrontFlash={toggleFrontFlash}
          onToggleAutoSave={toggleAutoSave}
          onToggleAutoShare={toggleAutoShare}
          onShowHint={() => setShowHint(true)}
          onOpenPaywall={() => setShowPaywall(true)}
        />
      )}

      {/* Gallery modal */}
      {showGallery && (
        <Gallery
          onClose={() => {
            setShowGallery(false);
            countPhotos().then(setGalleryCount).catch(() => {});
          }}
        />
      )}

      {/* Where-saved hint */}
      {showHint && <WhereSavedHint onClose={dismissHint} />}

      {/* Paywall */}
      <PaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        onUnlock={handleUnlock}
        onRestored={() => {
          setIsPremium(true);
          setShowPaywall(false);
        }}
        loading={checkoutLoading}
        freeLimit={freeLimit}
      />

      {/* BOTTOM CONTROLS — repositioned in landscape */}
      <div
        data-testid="bottom-controls"
        data-orientation={orientation}
        className={
          isLandscape
            ? `absolute top-0 bottom-0 ${
                isLandscapeLeft ? "right-0" : "left-0"
              } w-[120px] z-30 p-4 flex flex-col items-center justify-center gap-6 bg-gradient-to-${
                isLandscapeLeft ? "l" : "r"
              } from-black/85 via-black/40 to-transparent`
            : "absolute bottom-0 left-0 right-0 z-30 p-6 pb-10 flex flex-col gap-5 bg-gradient-to-t from-black/85 via-black/40 to-transparent"
        }
      >
        {/* Info row — only in portrait (no room in landscape) */}
        {!isLandscape && (
          <div className="flex items-center justify-center">
            <p className="font-mono-label text-white/55 text-center">
              {t("previewMirrored")}
            </p>
          </div>
        )}

        {/* Action row */}
        <div
          className={
            isLandscape
              ? "flex flex-col items-center gap-6"
              : "flex items-center justify-between px-2"
          }
        >
          {/* Filter toggle */}
          <button
            data-testid="filter-toggle-button"
            onClick={() => setShowFilters((s) => !s)}
            aria-label="Filters"
            className={`h-14 w-14 rounded-full backdrop-blur-md border flex items-center justify-center active:scale-95 transition-all ${
              showFilters
                ? "bg-white text-black border-white"
                : "bg-black/40 text-white border-white/10"
            }`}
          >
            <SlidersHorizontal
              className="h-5 w-5"
              strokeWidth={1.8}
            />
          </button>

          {/* SHUTTER */}
          <button
            data-testid="capture-shutter-button"
            onClick={startCountdownThenCapture}
            disabled={!ready || busy}
            aria-label="Capture"
            className="relative h-20 w-20 rounded-full border-4 border-white flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
          >
            <span
              className={`h-[60px] w-[60px] rounded-full transition-all ${
                busy ? "bg-red-500 scale-50" : "bg-white"
              }`}
            />
          </button>

          {/* Flip camera + Settings (3-dots) cluster
              Portrait: 3-dots above flip
              Landscape: 3-dots beside flip toward center */}
          <div
            className={
              isLandscape
                ? `flex items-center gap-3 ${
                    isLandscapeLeft ? "flex-row" : "flex-row-reverse"
                  }`
                : "flex flex-col items-center gap-3"
            }
          >
            {!error && (
              <button
                data-testid="settings-menu-button"
                onClick={() => setShowSettings(true)}
                aria-label="Settings"
                className="h-11 w-11 rounded-full bg-black/55 backdrop-blur-md border border-white/15 flex items-center justify-center active:scale-95 transition-transform shadow-[0_4px_16px_rgba(0,0,0,0.4)]"
              >
                <MoreVertical className="h-5 w-5 text-white" strokeWidth={1.8} />
              </button>
            )}
            <button
              data-testid="toggle-camera-button"
              onClick={flipCamera}
              aria-label="Flip camera"
              className="h-14 w-14 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center active:scale-95 transition-transform"
            >
              <RefreshCw className="h-5 w-5 text-white" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Brand — only in portrait */}
        {!isLandscape && (
          <div className="flex flex-col items-center gap-1 pt-1">
            <div className="flex items-center justify-center gap-2">
              <Camera className="h-3 w-3 text-white/40" strokeWidth={2} />
              <span className="font-mono-label text-white/40">Real Selfie</span>
            </div>
            <div className="flex items-center gap-2 text-[9px] text-white/25 tracking-wider uppercase">
              <a href="/terms" className="hover:text-white/60 transition-colors">
                Podmienky
              </a>
              <span>·</span>
              <a href="/privacy" className="hover:text-white/60 transition-colors">
                Ochrana údajov
              </a>
              <span>·</span>
              <a href="/cookies" className="hover:text-white/60 transition-colors">
                Cookies
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Pre-capture front-flash illumination overlay */}
      {preFlash && (
        <div
          data-testid="pre-flash-overlay"
          className="absolute inset-0 bg-white z-[55] pointer-events-none"
          style={{ opacity: 0.92 }}
        />
      )}

      {/* Shutter flash */}
      {flash && (
        <div
          data-testid="shutter-flash-overlay"
          className="absolute inset-0 bg-white z-[60] pointer-events-none shutter-flash"
        />
      )}
    </div>
  );
}

// Aspect ratio visual mask — darkens area outside the target crop.
// Ratio is portrait (width / height). Handles both orientations:
//   portrait → bottom controls take ~220px height, side padding 16px
//   landscape → side controls bar (~120px wide), vertical padding ~24px
function AspectMask({ aspectKey, isLandscape, isLandscapeLeft }) {
  const cfg = ASPECT_RATIOS.find((a) => a.key === aspectKey);
  if (!cfg || !cfg.ratio) return null;

  // ASPECT_RATIOS.ratio is stored as portrait (W/H). When the phone is in
  // landscape we invert so the user sees the frame oriented with the device
  // (e.g. 16:9 wide instead of 9:16 tall).
  const displayRatio = isLandscape ? 1 / cfg.ratio : cfg.ratio;

  // Reserve room for the side-mounted controls bar in landscape so the frame
  // doesn't get hidden behind it.
  const containerStyle = isLandscape
    ? {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: isLandscapeLeft ? 0 : 136,
        right: isLandscapeLeft ? 136 : 0,
      }
    : { position: "absolute", inset: 0 };

  const frameClass = isLandscape
    ? "border border-white/40 max-h-[calc(100dvh-48px)] max-w-[calc(100%-32px)]"
    : "border border-white/40 max-h-[calc(100dvh-220px)] max-w-[calc(100vw-32px)]";

  return (
    <div
      data-testid="aspect-mask"
      className="z-[15] pointer-events-none flex items-center justify-center"
      style={containerStyle}
    >
      <div
        data-testid={`aspect-frame-${aspectKey}`}
        className={frameClass}
        style={{
          aspectRatio: String(displayRatio),
          // In landscape vertical space is the limiting factor, so let height
          // drive sizing (aspectRatio derives the width). In portrait width
          // is the limit. Setting only one dimension lets the CSS engine
          // honour aspect-ratio properly with the max-* clamps.
          height: isLandscape ? "100%" : undefined,
          width: isLandscape ? undefined : "100%",
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
        }}
      />
    </div>
  );
}
